import {
  LockIcon, Collapsible, PaddedCollapsible, FlexColumn,
} from '@equifinance/shared';
import {
  useCallback, PropsWithChildren,
} from 'react';
import styled from 'styled-components';
import {
  ChecklistItem, mapDecisionToStatus, useChecklistContext,
} from '../../../../contexts';
import { ChecklistSection, CollapsibleHeaderCss } from '../../ChecklistPage';
import { SectionTitleIcon } from '../../../Account/Icons/SectionTitleIcon';
import { TickPurpleCircleIconInverted, CrossCircleIconInverted } from '../../../Account/components/Icons';
import { SubHeader } from '../../components/SubHeader';
import { Notes } from '../../components/Notes';
import Section, { SectionProps } from '../../../Account/components/Section';
import { usePageConfigurationContext } from '../../../../contexts/pageConfiguration';

const Body = styled(FlexColumn)` ${({ theme }) => `
  gap: ${theme.padding[6]};
  padding: ${theme.padding[1]} ${theme.padding[2]} ${theme.padding[2]};
`}`;

type GenericSectionProps = SectionProps & PropsWithChildren<{
  section?: ChecklistSection,
  subSection?: ChecklistSection,
  checklistItem?: ChecklistItem | null,
}>;

export function GenericSection(props: GenericSectionProps) {
  const {
    section,
    subSection,
    children,
    checklistItem,
    CollapsibleComponent = Collapsible,
    title,
    ...rest
  } = props;

  const {
    expandedStates, handleToggle,
  } = useChecklistContext();

  const {
    id, status, notes, checklistIndicatorType,
  } = checklistItem ?? {};

  const renderSubHeader = useCallback((collapsed: boolean) => (
    <SubHeader collapsed={collapsed} status={mapDecisionToStatus(status!)} />
  ), [status]);

  const { isReadOnly } = usePageConfigurationContext();

  return (
    <Section
      id={id}
      title={title ?? section ?? subSection!}
      headerCss={CollapsibleHeaderCss}
      collapsed={!expandedStates[section ?? subSection!]}
      onClickCollapse={() => handleToggle(section ?? subSection!)}
      subHeader={section && renderSubHeader}
      titleIcon={!!section && (
        <SectionTitleIcon
          tickIcon={<TickPurpleCircleIconInverted />}
          crossIcon={<CrossCircleIconInverted />}
          lockIcon={<LockIcon inverted />}
          isLocked={(checklistIndicatorType === 'Locked') || isReadOnly}
          isCompleted={checklistIndicatorType === 'Complete'}
        />
      )}
      CollapsibleComponent={CollapsibleComponent}
      {...rest}
    >
      <Body>
        {children}
        {id && section && (
          <Notes id={id} notesSection={subSection!} notes={notes} />
        )}
      </Body>
    </Section>
  );
}

export function PaddedGenericSection(props: GenericSectionProps) {
  const { children } = props;
  return <GenericSection {...props} CollapsibleComponent={PaddedCollapsible}>{children}</GenericSection>;
}
